import errorHandler from "common/errorHandler";
import { showError, showSuccess } from "common/ToastNotifications";
import { call, put, takeEvery } from "redux-saga/effects";
import { Endpoints } from "services/api";
import * as actions from "./actions";

const successOn2xx = {
    validateStatus: (status) => status >= 200 && status < 300,
};

const buildQueryString = (queryParams = {}) => {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            params.append(key, value);
        }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : "";
};

function* getAnnotations(api, recordType, recordId, queryParams) {
    const queryString = buildQueryString(queryParams);
    const url = `${Endpoints.comments}/api/v1/annotations/record/${recordType}/${recordId}${queryString}`;
    const result = yield call([api, api.get], url);
    return result.data;
}

function* createAnnotation(api, recordType, recordId, annotationData) {
    const url = `${Endpoints.comments}/api/v1/annotations/record/${recordType}/${recordId}`;
    const result = yield call([api, api.post], url, annotationData, {}, successOn2xx);
    return result.data;
}

function* updateAnnotation(api, recordType, recordId, annotationId, annotationData) {
    const url = `${Endpoints.comments}/api/v1/annotations/record/${recordType}/${recordId}/${annotationId}`;
    const result = yield call([api, api.put], url, annotationData, {}, successOn2xx);
    return result.data;
}

function* deleteAnnotation(api, recordType, recordId, annotationId) {
    const url = `${Endpoints.comments}/api/v1/annotations/record/${recordType}/${recordId}/${annotationId}`;
    yield call([api, api.delete], url, null, {}, successOn2xx);
    return annotationId;
}

function* getMyAnnotations(api, queryParams) {
    const queryString = buildQueryString(queryParams);
    const url = `${Endpoints.comments}/api/v1/annotations/assigned${queryString}`;
    const result = yield call([api, api.get], url);
    return result.data;
}

export function* getAnnotationsRequestSaga(api, action) {
    try {
        const { recordType, recordId, queryParams } = action.payload;
        const annotations = yield call(getAnnotations, api, recordType, recordId, queryParams);
        const recordKey = `${recordType}:${recordId}`;
        yield put(actions.getAnnotationsSuccess(recordKey, annotations));
    } catch (error) {
        const errorObject = errorHandler(error, "Get", "annotations");
        yield put(actions.getAnnotationsFailure(errorObject));
    }
}

export function* createAnnotationRequestSaga(api, action) {
    try {
        const { recordType, recordId, annotationData } = action.payload;
        const newAnnotation = yield call(createAnnotation, api, recordType, recordId, annotationData);
        const recordKey = `${recordType}:${recordId}`;
        yield put(actions.createAnnotationSuccess(recordKey, newAnnotation));
        showSuccess("Annotation created successfully");
    } catch (error) {
        const errorObject = errorHandler(error, "Create", "annotation");
        showError("Failed to create annotation");
        yield put(actions.createAnnotationFailure(errorObject));
    }
}

export function* updateAnnotationRequestSaga(api, action) {
    try {
        const { recordType, recordId, annotationId, annotationData } = action.payload;
        const updatedAnnotation = yield call(updateAnnotation, api, recordType, recordId, annotationId, annotationData);
        const recordKey = `${recordType}:${recordId}`;
        yield put(actions.updateAnnotationSuccess(recordKey, updatedAnnotation));
        showSuccess("Annotation updated successfully");
    } catch (error) {
        const errorObject = errorHandler(error, "Update", "annotation");
        showError("Failed to update annotation");
        yield put(actions.updateAnnotationFailure(errorObject));
    }
}

export function* deleteAnnotationRequestSaga(api, action) {
    try {
        const { recordType, recordId, annotationId } = action.payload;
        yield call(deleteAnnotation, api, recordType, recordId, annotationId);
        const recordKey = `${recordType}:${recordId}`;
        yield put(actions.deleteAnnotationSuccess(recordKey, annotationId));
        showSuccess("Annotation deleted successfully");
    } catch (error) {
        const errorObject = errorHandler(error, "Delete", "annotation");
        showError("Failed to delete annotation");
        yield put(actions.deleteAnnotationFailure(errorObject));
    }
}

export function* getMyAnnotationsRequestSaga(api, action) {
    try {
        const { queryParams } = action.payload;
        const annotations = yield call(getMyAnnotations, api, queryParams);
        yield put(actions.getMyAnnotationsSuccess(annotations));
    } catch (error) {
        const errorObject = errorHandler(error, "Get", "my annotations");
        yield put(actions.getMyAnnotationsFailure(errorObject));
    }
}

export default function* ({ api }) {
    yield takeEvery(actions.GET_ANNOTATIONS_REQUEST, getAnnotationsRequestSaga, api);
    yield takeEvery(actions.CREATE_ANNOTATION_REQUEST, createAnnotationRequestSaga, api);
    yield takeEvery(actions.UPDATE_ANNOTATION_REQUEST, updateAnnotationRequestSaga, api);
    yield takeEvery(actions.DELETE_ANNOTATION_REQUEST, deleteAnnotationRequestSaga, api);
    yield takeEvery(actions.GET_MY_ANNOTATIONS_REQUEST, getMyAnnotationsRequestSaga, api);
}
